import { useState, useCallback } from 'react';
import { compareFieldValues } from 'src/components/UI/input/changed-field';
import { resolveVitalStatus } from '../pedigree-utils';

const emptyEntry = () => ({
  genderIdentity: null, // null = unchanged
  isAlive: null,
  dob: null,
  dod: null,
  firstName: null,
  lastName: null,
  ethnicity: null,
  mobile: null,
  email: null,
  hospitalNumber: null,
  healthcareId: null,
  isHide: null,
  recordsReviewed: null,
  newDiseases: [],
  removedDiseaseIds: [],
  newPhenotypes: [],
  removedPhenotypeIds: [],
  newGenotypes: [],
  removedGenotypeIds: []
});

// ── What is unsaved (TGCLOUD-1902) ─────────────────────────────────────────
//
// A row's person field is an unsaved change while its draft DIFFERS from the
// chart's row by value (rule 1), not merely while it has a draft: a value typed
// back to what is saved is no change, and is neither drawn, counted nor written.
// The chart's row is the value last saved, as the chart holds it.

/** The person fields a row drafts. */
export const PERSON_FIELD_KEYS = Object.freeze([
  'genderIdentity',
  'isAlive',
  'dob',
  'dod',
  'firstName',
  'lastName',
  'ethnicity',
  'mobile',
  'email',
  'hospitalNumber',
  'healthcareId',
  'isHide',
  'recordsReviewed'
]);

const GENDER_IDENTITY_ALIASES = {
  'Unknown/Not Stated Unknown': 'Unknown/Not Stated',
  male: 'Male',
  female: 'Female',
  unknown: 'Unknown/Not Stated'
};

/** A gender identity as the row's select holds it. */
export const normaliseGenderIdentity = (value) =>
  value ? GENDER_IDENTITY_ALIASES[value] ?? value : '';

const COMPARE = {
  dob: { kind: 'date' },
  dod: { kind: 'date' },
  isHide: { kind: 'flag' },
  recordsReviewed: { kind: 'flag' }
};

/** A person field of the chart's row, as the row's cell shows it. */
export function savedPersonValue(member, key) {
  switch (key) {
    case 'genderIdentity':
      return normaliseGenderIdentity(member?.genderIdentity ?? member?.gender);
    case 'isAlive':
      return resolveVitalStatus({
        isAlive: member?.isAlive,
        dod: member?.dod || ''
      });
    case 'isHide':
    case 'recordsReviewed':
      return Boolean(member?.[key]);
    case 'ethnicity':
      return member?.ethnicity || [];
    default:
      return member?.[key] ?? '';
  }
}

/**
 * The person fields whose draft differs from the chart's row.
 * @returns {string[]} draft keys, in PERSON_FIELD_KEYS order
 */
export function changedPersonFields(entry, member) {
  if (!entry || !member) return [];
  return PERSON_FIELD_KEYS.filter((key) => {
    const value = entry[key];
    if (value === null || value === undefined) return false;
    const drafted =
      key === 'genderIdentity' ? normaliseGenderIdentity(value) : value;
    return !compareFieldValues(
      savedPersonValue(member, key),
      drafted,
      COMPARE[key] || {}
    );
  });
}

/** The clinical records a row adds or removes. */
export function clinicalChangeCount(entry) {
  if (!entry) return 0;
  return (
    entry.newDiseases.length +
    entry.removedDiseaseIds.length +
    entry.newPhenotypes.length +
    entry.removedPhenotypeIds.length +
    entry.newGenotypes.length +
    entry.removedGenotypeIds.length
  );
}

/** Every unsaved change on a row: its changed fields and its record changes. */
export const rowChangeCount = (entry, member) =>
  changedPersonFields(entry, member).length + clinicalChangeCount(entry);

export function useBulkEditState() {
  const [draft, setDraft] = useState(new Map());

  const getEntry = useCallback(
    (pid) => draft.get(pid) || emptyEntry(),
    [draft]
  );

  // A row holds an unsaved change (the chart's row passed whole: the draft is
  // compared with it).
  const isDirty = useCallback(
    (member) => rowChangeCount(draft.get(member?.pid), member) > 0,
    [draft]
  );

  const dirtyCount = useCallback(
    (memberRecords) => memberRecords.filter((m) => isDirty(m)).length,
    [isDirty]
  );

  // How many unsaved changes the rows hold: "N unsaved changes".
  const changeCount = useCallback(
    (memberRecords) =>
      memberRecords.reduce(
        (sum, m) => sum + rowChangeCount(draft.get(m.pid), m),
        0
      ),
    [draft]
  );

  const patch = useCallback((pid, fields) => {
    setDraft((prev) => {
      const next = new Map(prev);
      next.set(pid, { ...(next.get(pid) || emptyEntry()), ...fields });
      return next;
    });
  }, []);

  const updatePersonField = useCallback(
    (pid, field, value) => patch(pid, { [field]: value }),
    [patch]
  );

  const updatePersonFields = useCallback(
    (pid, fields) => patch(pid, fields),
    [patch]
  );

  // ── Diseases ──────────────────────────────────────────────────────────────

  const addDisease = useCallback((pid, disease) => {
    setDraft((prev) => {
      const entry = prev.get(pid) || emptyEntry();
      if (entry.newDiseases.some((d) => d.id === disease.id)) return prev;
      const next = new Map(prev);
      next.set(pid, {
        ...entry,
        newDiseases: [...entry.newDiseases, disease]
      });
      return next;
    });
  }, []);
  const setNewDiseaseAge = useCallback((pid, diseaseId, age) => {
    setDraft((prev) => {
      const entry = prev.get(pid) || emptyEntry();
      const next = new Map(prev);
      next.set(pid, {
        ...entry,
        newDiseases: entry.newDiseases.map((d) =>
          d.id === diseaseId ? { ...d, age: age || undefined } : d
        )
      });
      return next;
    });
  }, []);

  const removeNewDisease = useCallback((pid, diseaseId) => {
    setDraft((prev) => {
      const entry = prev.get(pid) || emptyEntry();
      const next = new Map(prev);
      next.set(pid, {
        ...entry,
        newDiseases: entry.newDiseases.filter((d) => d.id !== diseaseId)
      });
      return next;
    });
  }, []);

  // _id is the MongoDB id used for delete
  const markDiseaseRemoved = useCallback((pid, mongoId) => {
    setDraft((prev) => {
      const entry = prev.get(pid) || emptyEntry();
      if (entry.removedDiseaseIds.includes(mongoId)) return prev;
      const next = new Map(prev);
      next.set(pid, {
        ...entry,
        removedDiseaseIds: [...entry.removedDiseaseIds, mongoId]
      });
      return next;
    });
  }, []);

  const unmarkDiseaseRemoved = useCallback((pid, mongoId) => {
    setDraft((prev) => {
      const entry = prev.get(pid) || emptyEntry();
      const next = new Map(prev);
      next.set(pid, {
        ...entry,
        removedDiseaseIds: entry.removedDiseaseIds.filter(
          (id) => id !== mongoId
        )
      });
      return next;
    });
  }, []);

  // ── Phenotypes ────────────────────────────────────────────────────────────

  const addPhenotype = useCallback((pid, phenotype) => {
    setDraft((prev) => {
      const entry = prev.get(pid) || emptyEntry();
      if (entry.newPhenotypes.some((p) => p.id === phenotype.id)) return prev;
      const next = new Map(prev);
      next.set(pid, {
        ...entry,
        newPhenotypes: [...entry.newPhenotypes, phenotype]
      });
      return next;
    });
  }, []);

  const removeNewPhenotype = useCallback((pid, phenotypeId) => {
    setDraft((prev) => {
      const entry = prev.get(pid) || emptyEntry();
      const next = new Map(prev);
      next.set(pid, {
        ...entry,
        newPhenotypes: entry.newPhenotypes.filter((p) => p.id !== phenotypeId)
      });
      return next;
    });
  }, []);

  const markPhenotypeRemoved = useCallback((pid, phenotypeId) => {
    setDraft((prev) => {
      const entry = prev.get(pid) || emptyEntry();
      if (entry.removedPhenotypeIds.includes(phenotypeId)) return prev;
      const next = new Map(prev);
      next.set(pid, {
        ...entry,
        removedPhenotypeIds: [...entry.removedPhenotypeIds, phenotypeId]
      });
      return next;
    });
  }, []);

  // ── Genotypes ─────────────────────────────────────────────────────────────

  const addGenotype = useCallback((pid, genotype) => {
    setDraft((prev) => {
      const entry = prev.get(pid) || emptyEntry();
      if (entry.newGenotypes.some((g) => g.id === genotype.id)) return prev;
      const next = new Map(prev);
      next.set(pid, {
        ...entry,
        newGenotypes: [...entry.newGenotypes, genotype]
      });
      return next;
    });
  }, []);

  const removeNewGenotype = useCallback((pid, genotypeId) => {
    setDraft((prev) => {
      const entry = prev.get(pid) || emptyEntry();
      const next = new Map(prev);
      next.set(pid, {
        ...entry,
        newGenotypes: entry.newGenotypes.filter((g) => g.id !== genotypeId)
      });
      return next;
    });
  }, []);

  const markGenotypeRemoved = useCallback((pid, genotypeId) => {
    setDraft((prev) => {
      const entry = prev.get(pid) || emptyEntry();
      if (entry.removedGenotypeIds.includes(genotypeId)) return prev;
      const next = new Map(prev);
      next.set(pid, {
        ...entry,
        removedGenotypeIds: [...entry.removedGenotypeIds, genotypeId]
      });
      return next;
    });
  }, []);

  const reset = useCallback(() => setDraft(new Map()), []);

  // After a save: what was not written stays drafted, and so drawn (rule 7).
  // `kept` maps a pid to the parts of its entry that failed.
  const keepOnly = useCallback(
    (kept) =>
      setDraft(
        new Map(
          [...kept].map(([pid, part]) => [pid, { ...emptyEntry(), ...part }])
        )
      ),
    []
  );

  return {
    draft,
    getEntry,
    isDirty,
    dirtyCount,
    changeCount,
    updatePersonField,
    updatePersonFields,
    addDisease,
    setNewDiseaseAge,
    removeNewDisease,
    markDiseaseRemoved,
    unmarkDiseaseRemoved,
    addPhenotype,
    removeNewPhenotype,
    markPhenotypeRemoved,
    addGenotype,
    removeNewGenotype,
    markGenotypeRemoved,
    reset,
    keepOnly
  };
}
